import React, { useState } from 'react';
import { useInventory } from '../../context/InventoryContext';
import { useAuth } from '../../context/AuthContext';
import Select from '../ui/Select';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../ui/Card';

interface TransactionFormProps {
  onClose: () => void;
}

const STOCK_IN_REASONS = [
  { value: 'purchase_order', label: 'Purchase Order Receipt' },
  { value: 'customer_return', label: 'Return from Customer' },
  { value: 'inventory_adjustment_plus', label: 'Inventory Count Adjustment (+)' },
  { value: 'qc_release', label: 'Quality Control Release' },
  { value: 'transfer_in', label: 'Transfer In from Other Location' },
  { value: 'production_completion', label: 'Production Completion' },
  { value: 'sample_return', label: 'Sample Return' },
  { value: 'other', label: 'Other' },
];

const STOCK_OUT_REASONS = [
  { value: 'customer_order', label: 'Customer Order Fulfillment' },
  { value: 'damaged', label: 'Damaged/Defective Items' },
  { value: 'inventory_adjustment_minus', label: 'Inventory Count Adjustment (-)' },
  { value: 'qc_hold', label: 'Quality Control Hold' },
  { value: 'transfer_out', label: 'Transfer Out to Other Location' },
  { value: 'production_usage', label: 'Production Usage' },
  { value: 'sample_distribution', label: 'Sample Distribution' },
  { value: 'theft_loss', label: 'Theft/Loss' },
  { value: 'expiration', label: 'Expiration/Obsolescence' },
  { value: 'other', label: 'Other' },
];

const TransactionForm: React.FC<TransactionFormProps> = ({ onClose }) => {
  const { products, addTransaction } = useInventory();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    productId: '',
    type: 'in',
    quantity: 1,
    reasonCode: '',
    notes: '',
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.productId) {
      newErrors.productId = 'Product is required';
    }
    
    if (!formData.type) {
      newErrors.type = 'Transaction type is required';
    }

    if (!formData.reasonCode) {
      newErrors.reasonCode = 'Reason code is required';
    }
    
    if (formData.quantity <= 0) {
      newErrors.quantity = 'Quantity must be greater than 0';
    }
    
    if (formData.reasonCode === 'other' && !formData.notes.trim()) {
      newErrors.notes = 'Notes are required when selecting Other';
    }
    
    // Additional validation for 'out' transactions
    if (formData.type === 'out') {
      const product = products.find(p => p.id === formData.productId);
      if (product && formData.quantity > product.stock_quantity) {
        newErrors.quantity = `Cannot remove more than current stock (${product.stock_quantity})`;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSelectChange = (name: string) => (value: string) => {
    setFormData(prev => {
      // Reset reason code when changing transaction type
      if (name === 'type') {
        return { ...prev, [name]: value, reasonCode: '' };
      }
      return { ...prev, [name]: value };
    });
    setErrors(prev => ({ ...prev, [name]: '' }));
  };
  
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const metadata = {
        reason_code: formData.reasonCode,
        username: user?.email?.split('@')[0] || 'unknown',
        timestamp: new Date().toISOString(),
      };

      await addTransaction({
        product_id: formData.productId,
        type: formData.type as 'in' | 'out',
        quantity: formData.quantity,
        notes: formData.notes || formData.reasonCode,
        user_reference: user?.email?.split('@')[0] || 'unknown',
        metadata,
      });
      
      onClose();
    } catch (error) {
      console.error('Error adding transaction:', error);
      setErrors(prev => ({ ...prev, submit: 'Failed to add transaction' }));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const selectedProduct = products.find(p => p.id === formData.productId);

  const productOptions = products.map(product => ({
    value: product.id,
    label: `${product.name} (SKU: ${product.sku})`,
  }));
  
  const typeOptions = [
    { value: 'in', label: 'Stock In' },
    { value: 'out', label: 'Stock Out' },
  ];
  
  const reasonOptions = formData.type === 'in' ? STOCK_IN_REASONS : STOCK_OUT_REASONS;
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Add New Transaction</CardTitle>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <div className="bg-gray-50 p-3 rounded-lg mb-4">
            <p className="text-sm text-gray-600">
              Transaction by: <span className="font-medium text-gray-900">{user?.email?.split('@')[0]}</span>
            </p>
          </div>

          <Select
            label="Product"
            options={productOptions}
            value={formData.productId}
            onChange={handleSelectChange('productId')}
            error={errors.productId}
            fullWidth
            required
          />
          
          <Select
            label="Transaction Type"
            options={typeOptions}
            value={formData.type}
            onChange={handleSelectChange('type')}
            error={errors.type}
            fullWidth
            required
          />

          <Select
            label="Reason"
            options={reasonOptions}
            value={formData.reasonCode}
            onChange={handleSelectChange('reasonCode')}
            error={errors.reasonCode}
            fullWidth
            required
          />
          
          <Input
            label={`Quantity${selectedProduct ? ` (${selectedProduct.unit_of_measure})` : ''}`}
            name="quantity"
            type="number"
            min="1"
            value={formData.quantity.toString()}
            onChange={handleNumberChange}
            error={errors.quantity}
            helperText={selectedProduct ? `Enter the quantity in ${selectedProduct.unit_of_measure}` : undefined}
            fullWidth
            required
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes {formData.reasonCode === 'other' && <span className="text-red-500">*</span>}
            </label>
            <textarea
              name="notes"
              rows={3}
              value={formData.notes}
              onChange={handleInputChange}
              className={`w-full rounded-md border ${
                errors.notes ? 'border-red-500' : 'border-gray-300'
              } px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900`}
              placeholder={formData.reasonCode === 'other' ? 'Please provide details for this transaction' : 'Optional notes about this transaction'}
            />
            {errors.notes && (
              <p className="mt-1 text-xs text-red-500">{errors.notes}</p>
            )}
          </div>

          {errors.submit && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {errors.submit}
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting || Object.keys(errors).length > 0}
          >
            Add Transaction
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default TransactionForm;